import {
  CheckOutlined,
  CloseOutlined,
  CopyTwoTone,
  DeleteOutlined,
  EditTwoTone,
  OrderedListOutlined,
} from "@ant-design/icons";
import {
  Descriptions,
  message,
  Popconfirm,
  Space,
  Table,
  Tag,
  Tooltip,
} from "antd";
import useBreakpoint from "antd/lib/grid/hooks/useBreakpoint";
import React from "react";
import CopyToClipboard from "react-copy-to-clipboard";
import { Link } from "react-router-dom";

interface ShopTableProps {
  data: any[];
  updateShopActive: (shopId: string, status: boolean) => void;
  onDeleteShop: (shopId: string) => void;
}

interface ExpandedRowProps {
  name: string;
  _id: string;
  opening_hour: string;
  closing_hour: string;
  isActive: boolean;
}

const { Column } = Table;

const ExpandedRow: React.FC<ExpandedRowProps> = ({
  name,
  _id,
  opening_hour,
  closing_hour,
  isActive,
}) => {
  return (
    <Descriptions layout="horizontal" column={1} size="small">
      <Descriptions.Item label="Id">{_id}</Descriptions.Item>
      <Descriptions.Item label="Name">{name}</Descriptions.Item>
      <Descriptions.Item label="Hours">
        {opening_hour}:00-{closing_hour}:00
      </Descriptions.Item>
      <Descriptions.Item label="Active">
        <Tag color={isActive ? "green" : "red"}>
          {isActive ? "Active" : "Inactive"}
        </Tag>
      </Descriptions.Item>
    </Descriptions>
  );
};

const ShopTable: React.FC<ShopTableProps> = ({
  data,
  updateShopActive,
  onDeleteShop,
}) => {
  const { md } = useBreakpoint();

  return (
    <Table
      dataSource={data}
      rowKey="_id"
      pagination={{
        pageSize: 10,
        showSizeChanger: true,
        pageSizeOptions: ["10", "20", "50"],
        showTotal: (total) => `${total} shops`,
      }}
      size="middle"
      bordered
      expandable={
        !md
          ? {
              expandedRowRender: (record: any) => <ExpandedRow {...record} />,
            }
          : undefined
      }
    >
      {md && (
        <Column
          ellipsis
          title="Id"
          dataIndex="_id"
          key="_id"
          render={(id) => (
            <div>
              <CopyToClipboard
                onCopy={() => message.success("Copied", 0.4)}
                text={id}
              >
                <CopyTwoTone className="mr-8" />
              </CopyToClipboard>
              {id}
            </div>
          )}
        />
      )}
      <Column ellipsis title="Shop Name" dataIndex="name" key="name" />
      {md && (
        <Column
          ellipsis
          title="Hours"
          key="hours"
          width={140}
          render={(_, record: any) => (
            <span>
              {record.opening_hour}:00-{record.closing_hour}:00
            </span>
          )}
        />
      )}
      {md && (
        <Column
          ellipsis
          title="Active"
          key="isActive"
          width={120}
          dataIndex="isActive"
          align="center"
          render={(isActive, record: any) => (
            <Tag color={isActive ? "green" : "red"}>
              <Space size="small">
                {isActive ? <CheckOutlined /> : <CloseOutlined />}
                {isActive ? "Active" : "Inactive"}
              </Space>
            </Tag>
          )}
        />
      )}
      {md && (
        <Column
          ellipsis
          title="Toggle"
          key="toggle"
          width={120}
          align="center"
          render={(_, record: any) => (
            <span
              style={{ cursor: "pointer" }}
              onClick={() => updateShopActive(record._id, !record.isActive)}
            >
              {record.isActive ? "Deactivate" : "Activate"}
            </span>
          )}
        />
      )}
      <Column
        ellipsis
        title=""
        key="actions"
        align="center"
        width={140}
        render={(_, record: any) => (
          <Space size="middle">
            <Tooltip title="Edit shop">
              <Link to={"/shops/edit/" + record._id}>
                <EditTwoTone className="fs-18" />
              </Link>
            </Tooltip>
            <Tooltip title="View orders">
              <Link to={"/shops/orders/" + record._id}>
                <OrderedListOutlined className="fs-18" />
              </Link>
            </Tooltip>
            <Popconfirm
              title={`Delete "${record.name}"?`}
              okText="Delete"
              cancelText="Cancel"
              okType="danger"
              onConfirm={() => onDeleteShop(record._id)}
            >
              <Tooltip title="Delete shop">
                <DeleteOutlined
                  style={{ color: "#ff4d4f" }}
                  className="fs-18"
                />
              </Tooltip>
            </Popconfirm>
          </Space>
        )}
      />
    </Table>
  );
};

export default ShopTable;
